import React, {
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  CenteredSpaceBetweenRow,
  FullWidthJustifyEndRow,
  SpaceBetweenRow,
} from 'spw-shared/globalComponents/grid';
import {
  GridRow,
  GridColumn,
  RowTitle,
} from 'spw-programming-projects/building-details/worksheet/styles';
import {
  MultiplierBox,
  SpaceTypeNameContainer,
  WorksheetInput,
} from 'spw-components/worksheet/styles';
import { useSelector } from 'react-redux';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import {
  CUSTOM_PREFIX,
  MAX_DIM,
  MAX_QTY,
  MAX_SEAT_CAP,
  MAX_USF,
} from 'spw-constants';
import { CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH } from 'spw-programming-projects/constants';
import {
  isWholeNumber,
  useSpaceTypeInputValue,
} from 'spw-programming-projects/building-details/worksheet/utils';
import {
  breakLine,
  textAreaReachedMaxRows,
} from 'spw-programming-projects/building-details/worksheet/parent-space-type-row';
import {
  useQuantityRounding,
  useSpaceTypeRowUI,
} from 'spw-programming-projects/building-details/worksheet/hooks';
import WorksheetValuesInput from 'spw-programming-projects/building-details/worksheet/worksheet-values-input';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  asPercentageFixed,
  asPercentageRounded,
  numberWithCommas,
  numberWithCommasOrDash,
} from 'spw-shared/utils';
import { WorksheetContext } from 'spw-programming-projects/building-details/worksheet/worksheet-context';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import WorksheetRow from '../../../../models/WorksheetRow';
import QuantityRoundingPopup from './quantity-rounding-popup';

type SpaceTypeRowProps = {
  row: WorksheetRow;
  totalCategorySeats: number;
  isPerDepartmentRow?: boolean;
  active?: boolean;
  isDraggable?: boolean;
};

const SpaceTypeRow: React.FC<SpaceTypeRowProps> = ({
  row,
  totalCategorySeats,
  isPerDepartmentRow,
  isDraggable,
}) => {
  const testingSuffix = row.child
    ? `child_${row.spaceType.id}`
    : row.spaceType.id;

  const { name: spaceTypeName } = row.spaceType;
  const clientFacingName = row?.projectSpaceType?.clientFacingName;
  const inputRef: { current: HTMLInputElement } = React.useRef();
  const quantityInputRef = React.useRef<HTMLInputElement>();

  const {
    showDecimals,
    clientFacingNamesAreVisible,
    activeRowIdentifier,
    onRowNameFocus,
    onRowNameBlur,
    changeRow,
  } = useContext(WorksheetContext);
  const { convertToUnit } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);
  const [roundingPopupAnchorEl, setRoundingPopupAnchorEl] = useState<
    HTMLInputElement
  >(null);

  const active =
    activeRowIdentifier === `${row?.projectSpaceType?.id}${!!row.child}`;

  const decimalsForDimensionsEnabled =
    measurementSystem === MeasurementSystem.imperial;

  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const { totalSeatsPercentage, rowTotalDisplay } = useMemo(() => {
    const totalSeats = row.seatCap * row.quantity || 0;

    let totalDisplay: number | string = showDecimals ? '0.00' : '0';

    if (totalSeats) {
      totalDisplay = numberWithCommasOrDash(totalSeats, showDecimals);
    }

    let percentage: number | string = showDecimals ? '0.00%' : '0%';

    if (totalSeats > 0) {
      percentage = showDecimals
        ? asPercentageFixed(totalSeats / totalCategorySeats)
        : asPercentageRounded(totalSeats / totalCategorySeats);
    }
    return {
      totalSeatsPercentage: percentage,
      rowTotalDisplay: totalDisplay,
    };
  }, [row, showDecimals, totalCategorySeats]);

  const isCustomSpaceType = (row.spaceType?.spaceType as string)?.includes(
    CUSTOM_PREFIX
  );

  const {
    editFactor,
    editValue,
    setEditValue,
    name,
    setName,
    setHover,
    isHoveringName,
    setIsHoveringName,
    onFocusRowInput,
    onFocusRowName,
    onBlurNameInput,
    onBlurRatioInput,
    onBlurSeatCapInput,
    onBlurQuantityInput,
    onBlurUsfInput,
    handleSizeInputBlurred,
    handleInputChange,
    handleKeyPress,
    className,
    isEditingName,
    isRatio,
    spaceNameIsDuplicated,
    isDisabled,
  } = useSpaceTypeRowUI({
    row,
    inputRef,
    onRowNameFocus,
    onRowNameBlur,
    changeRow,
    isCustomSpaceType,
    clientFacingNamesAreVisible,
    isPerDepartmentRow,
    active,
  });

  const { handleRoundQuantity } = useQuantityRounding({
    quantityInputRef,
    setEditValue,
    editValue,
  });

  useEffect(() => {
    setName(
      clientFacingNamesAreVisible && clientFacingName
        ? clientFacingName
        : spaceTypeName
    );
  }, [clientFacingNamesAreVisible, clientFacingName, spaceTypeName, setName]);

  const rowTotalUsfVal = row.quantity * row.usf;

  const { getSpaceTypeInputValue } = useSpaceTypeInputValue({
    showDecimals,
    rowName: name,
    editFactor,
    editValue,
  });

  const ratioValue = getSpaceTypeInputValue('ratio', row.ratio);
  const quantityValue = getSpaceTypeInputValue('quantity', row.quantity);
  const seatCapValue = getSpaceTypeInputValue('seatCap', row.seatCap);
  const usfValue = getSpaceTypeInputValue(
    'usf',
    convertToUnit(row.usf, { unitType: 'area' }) as number
  );
  const sizeXValue =
    getSpaceTypeInputValue(
      'sizeX',
      convertToUnit(row.sizeX, {
        unitType: 'linear',
        outputIsFractionUnit: measurementSystem === MeasurementSystem.metric,
        inputIsMainUnit: true,
      }) as number,
      false,
      decimalsForDimensionsEnabled
    ) || '';
  const sizeYValue =
    getSpaceTypeInputValue(
      'sizeY',
      convertToUnit(row.sizeY, {
        unitType: 'linear',
        outputIsFractionUnit: measurementSystem === MeasurementSystem.metric,
        inputIsMainUnit: true,
      }) as number,
      false,
      decimalsForDimensionsEnabled
    ) || '';

  const shouldShowUSFError =
    editFactor !== `${name}.quantity` &&
    parseInt(quantityValue, 10) > 0 &&
    parseFloat(usfValue) * 100 < 1;

  const handleInputMouseDown = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
  };
  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (clientFacingNamesAreVisible || isCustomSpaceType) {
        setIsHoveringName(true);
        e.stopPropagation();
      }
    },
    [clientFacingNamesAreVisible, isCustomSpaceType, setIsHoveringName]
  );

  return (
    <GridRow
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`${className} ${
        isDraggable && !currentUserIsViewOnly ? 'draggable' : ''
      }`}
      data-testid={`worksheet_row_${spaceTypeName}`}
      data-cy={`worksheet_${row.child ? 'child_' : ''}row_${spaceTypeName}`}
    >
      <GridColumn xs={3} className="row-start">
        <SpaceBetweenRow style={{ width: '100%' }}>
          {row.title ? (
            <div
              style={{
                marginLeft: row.child ? 30 : 0,
                width: 'calc(100% - 30px)',
              }}
            >
              <RowTitle>
                <p>{row.title}</p>
                <p className="subtitle">{row.subtitle}</p>
              </RowTitle>
            </div>
          ) : (
            <FullWidthJustifyEndRow
              onClick={onFocusRowName}
              data-testid={`worksheet_row_name_${testingSuffix}`}
              style={{
                paddingLeft: 21,
                paddingRight: 8,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  width: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
                  minWidth: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
                }}
                data-cy={`worksheet_${
                  row.child ? 'child_' : ''
                }row_space_type_name_${spaceTypeName}`}
                data-testid={`worksheet_row_space_type_name_${spaceTypeName}`}
              >
                {isEditingName ? (
                  <WorksheetInput
                    inputRef={inputRef}
                    onKeyPress={handleKeyPress}
                    className={`left-align ${
                      spaceNameIsDuplicated ? 'duplicated' : ''
                    }`}
                    multiline
                    variant="outlined"
                    value={editFactor === 'name' ? editValue : name}
                    onFocus={onFocusRowName}
                    onChange={(e) => {
                      setEditValue(
                        textAreaReachedMaxRows(
                          e.target as HTMLTextAreaElement,
                          editValue
                        )
                          ? editValue
                          : e.target.value
                      );
                    }}
                    onBlur={onBlurNameInput}
                    onMouseDown={handleInputMouseDown}
                    inputProps={{
                      'data-testid': `worksheet_${
                        row.child ? 'child_' : ''
                      }row_space_type_name_input_${spaceTypeName}`,
                    }}
                  />
                ) : (
                  <SpaceTypeNameContainer
                    className={isHoveringName ? 'hover' : ''}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={() => setIsHoveringName(false)}
                    $disablePointer={currentUserIsViewOnly}
                  >
                    <div
                      style={{
                        maxHeight: 32,
                        overflow: 'hidden',
                      }}
                    >
                      <pre
                        style={{
                          maxHeight: 32,
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          whiteSpace: 'pre-wrap',
                          display: 'inline-flex',
                        }}
                      >
                        {breakLine(name)}
                      </pre>
                    </div>
                  </SpaceTypeNameContainer>
                )}
              </div>
            </FullWidthJustifyEndRow>
          )}
        </SpaceBetweenRow>
      </GridColumn>
      <GridColumn xs={1}>
        <WorksheetValuesInput
          endAdornment={isRatio ? undefined : '%'}
          startAdornment={isRatio ? '1:' : undefined}
          value={ratioValue}
          allowDecimals={showDecimals}
          groupSeparator=","
          disabled={isDisabled}
          onValueChange={(value) => handleInputChange(value, MAX_QTY)}
          onFocus={() => onFocusRowInput('ratio')}
          onBlur={onBlurRatioInput}
          onKeyPress={handleKeyPress}
          className={isRatio ? 'left-align' : ''}
          onMouseDown={handleInputMouseDown}
          data-testid={`worksheet_row_ratio_${testingSuffix}`}
          data-cy={`worksheet_${
            row.child ? 'child_' : ''
          }row_ratio_${spaceTypeName}`}
        />
      </GridColumn>
      <GridColumn xs={1}>
        <WorksheetValuesInput
          inputRef={quantityInputRef}
          error={!isWholeNumber(row.quantity)}
          value={quantityValue}
          allowDecimals={showDecimals}
          groupSeparator=","
          disabled={isDisabled}
          onValueChange={(value) => handleInputChange(value, MAX_QTY)}
          onBlur={() => {
            onBlurQuantityInput();
            setRoundingPopupAnchorEl(null);
          }}
          onFocus={(e) => {
            onFocusRowInput('quantity');
            if (!isWholeNumber(row.quantity) && showDecimals) {
              setRoundingPopupAnchorEl(e.currentTarget);
            }
          }}
          onKeyPress={handleKeyPress}
          onMouseDown={handleInputMouseDown}
          data-testid={`worksheet_row_qty_${testingSuffix}`}
          data-cy={`worksheet_${
            row.child ? 'child_' : ''
          }row_quantity_${spaceTypeName}`}
        />
        {roundingPopupAnchorEl && (
          <QuantityRoundingPopup
            anchorEl={roundingPopupAnchorEl}
            onRound={handleRoundQuantity}
          />
        )}
      </GridColumn>
      <GridColumn xs={1}>
        <WorksheetValuesInput
          value={seatCapValue}
          allowDecimals={showDecimals}
          groupSeparator=","
          disabled={isDisabled}
          onValueChange={(value) => handleInputChange(value, MAX_SEAT_CAP)}
          onBlur={onBlurSeatCapInput}
          onFocus={() => onFocusRowInput('seatCap')}
          onKeyPress={handleKeyPress}
          onMouseDown={handleInputMouseDown}
          data-testid={`worksheet_row_seatcap_${testingSuffix}`}
          data-cy={`worksheet_row_seatCap_${spaceTypeName}`}
        />
      </GridColumn>
      <GridColumn
        xs={1}
        data-testid={`worksheet_row_rowtotal_${testingSuffix}`}
      >
        {rowTotalDisplay}
      </GridColumn>
      <GridColumn
        xs={2}
        className="seat-percent-column nowrap"
        data-testid={`worksheet_row_seatpercent_${testingSuffix}`}
      >
        {totalSeatsPercentage}
      </GridColumn>
      <GridColumn xs={1}>
        <CenteredSpaceBetweenRow>
          <WorksheetValuesInput
            value={sizeXValue}
            allowDecimals={showDecimals && decimalsForDimensionsEnabled}
            groupSeparator=","
            disabled={isDisabled}
            onValueChange={(value) => handleInputChange(value, MAX_DIM)}
            onBlur={() => {
              handleSizeInputBlurred('sizeX');
            }}
            onFocus={(event) => onFocusRowInput('sizeX', event)}
            onKeyPress={handleKeyPress}
            className="width60"
            onMouseDown={handleInputMouseDown}
            data-testid={`worksheet_row_sizex_${testingSuffix}`}
            data-cy={`worksheet_row_sizeX_${spaceTypeName}`}
          />
        </CenteredSpaceBetweenRow>
        <MultiplierBox>X</MultiplierBox>
      </GridColumn>
      <GridColumn xs={1} className="justifyCenter">
        <WorksheetValuesInput
          value={sizeYValue}
          allowDecimals={showDecimals && decimalsForDimensionsEnabled}
          groupSeparator=","
          disabled={isDisabled}
          onValueChange={(value) => handleInputChange(value, MAX_DIM)}
          onBlur={() => handleSizeInputBlurred('sizeY')}
          onFocus={(event) => onFocusRowInput('sizeY', event)}
          onKeyPress={handleKeyPress}
          className="width60"
          onMouseDown={handleInputMouseDown}
          data-testid={`worksheet_row_sizey_${testingSuffix}`}
          data-cy={`worksheet_row_sizeY_${spaceTypeName}`}
        />
      </GridColumn>
      <GridColumn xs={1}>
        <WorksheetValuesInput
          value={usfValue}
          allowDecimals={showDecimals}
          groupSeparator=","
          disabled={isDisabled}
          error={shouldShowUSFError}
          onValueChange={(value) => handleInputChange(value, MAX_USF)}
          onBlur={onBlurUsfInput}
          onFocus={(event) => onFocusRowInput('usf', event)}
          onKeyPress={handleKeyPress}
          onMouseDown={handleInputMouseDown}
          data-testid={`worksheet_row_usf_${testingSuffix}`}
          data-cy={`worksheet_row_usf_${spaceTypeName}`}
        />
      </GridColumn>
      <GridColumn
        xs={1}
        className="row-end"
        data-testid="row-usf"
        data-cy={`worksheet_row_totalusf_${testingSuffix}`}
      >
        {convertToUnit(rowTotalUsfVal, {
          unitType: 'area',
          formatter: (val) => numberWithCommas(val, showDecimals, true),
        })}
      </GridColumn>
    </GridRow>
  );
};

export default SpaceTypeRow;
